export const FLUENTIZE_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "M1", "M2"];
export const FLUENTIZE_TASK_TYPES = [
  "Begin", "Fluency", "Vocabulary", "Grammar", "Talk", "Idiom",
  "Persuasion", "Culture", "Text", "Logic", "End",
];
export const TASK_REPEAT_GAP_MS = 2300;

const CLOCK = /^(\d{1,2}):([0-5]?\d):([0-5]?\d)$/;

export function parseTaskClock(value) {
  const match = CLOCK.exec(String(value ?? "").trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (hours > 23) return null;
  return (hours * 3600) + (minutes * 60) + seconds;
}

export function formatTaskClock(timeInSeconds) {
  const total = Math.max(0, Math.floor(Number(timeInSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
}

export function createFluentizeTaskDraft({ videoId, userLevel } = {}) {
  return {
    draft: true,
    transient: false,
    videoId: videoId || "",
    startTime: "00:00:00",
    stopTime: "00:00:00",
    text: "",
    repeatCount: 1,
    level: FLUENTIZE_LEVELS.includes(userLevel) ? userLevel : "A1",
    focus: "Begin",
  };
}

export function validateFluentizeTask(task, videoDuration = 0) {
  const errors = [];
  const start = parseTaskClock(task?.startTime);
  const stop = parseTaskClock(task?.stopTime);
  if (start == null) errors.push("Start Time must use hh:mm:ss.");
  if (stop == null) errors.push("Stop Time must use hh:mm:ss.");
  if (start != null && start < 0) errors.push("Start Time cannot be negative.");
  if (start != null && stop != null && !(stop > start)) {
    errors.push("Stop Time must be after Start Time.");
  }
  const duration = Number(videoDuration);
  if (
    start != null
    && stop != null
    && stop > start
    && duration > 0
    && stop > duration
  ) {
    errors.push("Stop Time is past the end of this video.");
  }
  const repeat = Number(task?.repeatCount);
  if (!Number.isInteger(repeat) || repeat < 1) {
    errors.push("Repeat Count must be at least 1.");
  }
  if (!FLUENTIZE_LEVELS.includes(task?.level)) errors.push("Choose a level.");
  if (!FLUENTIZE_TASK_TYPES.includes(task?.focus)) errors.push("Choose a Task Type.");
  if (!task?.videoId) errors.push("Choose a saved video first.");
  return {
    ok: errors.length === 0,
    errors,
    start,
    stop,
    repeat: Number.isInteger(repeat) ? repeat : 0,
  };
}

export const FLUENTIZE_SHARE_CORRECTION = "This Task needs to be corrected and saved before it can be shared.";
export const FLUENTIZE_SHARE_FAILURE = "Task could not be shared. Try again.";

export function fluentizeShareAuthorName(displayName, username) {
  const authName = typeof displayName === "string" ? displayName.trim() : "";
  const profileName = typeof username === "string" ? username.trim() : "";
  return authName || profileName || "Anonymous";
}

export function fluentizeTaskCanShare(task, { ownerId, videoDuration = 0 } = {}) {
  if (!task?.id || task.draft || task.transient) return false;
  if (task.userId && ownerId && task.userId !== ownerId) return false;
  return validateFluentizeTask(task, videoDuration).ok;
}

export function buildFluentizeSharePost(task, { authorId, authorDisplayName } = {}) {
  const notes = task?.text || "";
  return {
    content: [
      `📺 https://youtu.be/${task.videoId}`,
      `⏱️ ${task.startTime} → ${task.stopTime}`,
      `🎯 Notes: ${notes || "[None]"}`,
      `🔁 Repeat ${task.repeatCount}×`,
      `Level: ${task.level}, Focus: ${task.focus}`,
    ].join("\n"),
    taskData: {
      videoId: task.videoId,
      startTime: task.startTime,
      stopTime: task.stopTime,
      text: notes,
      repeatCount: Number(task.repeatCount),
      level: task.level,
      focus: task.focus,
    },
    imageUrl: null,
    likes: 0,
    shares: 0,
    comments: [],
    likedByUser: [],
    sharedByUser: [],
    role: "Learner",
    author: authorId,
    authorDisplayName: authorDisplayName || "Anonymous",
  };
}

export async function shareFluentizeTask({
  task,
  user,
  username,
  videoDuration = 0,
  inFlight,
  addPost,
  onPhase,
  onSerial,
  onWall,
  onNavigate,
} = {}) {
  if (inFlight?.current) return { ok: false, ignored: true };
  if (!user?.uid) return { ok: false, error: "Sign in to share this Task." };
  if (!fluentizeTaskCanShare(task, { ownerId: user.uid, videoDuration })) {
    return { ok: false, error: FLUENTIZE_SHARE_CORRECTION };
  }
  if (inFlight) inFlight.current = true;
  onPhase?.("sharing");
  try {
    const post = buildFluentizeSharePost(task, {
      authorId: user.uid,
      authorDisplayName: fluentizeShareAuthorName(user.displayName, username),
    });
    await addPost(post);
    onSerial?.();
    onPhase?.("succeeded");
    onWall?.();
    onNavigate?.();
    return { ok: true, post };
  } catch {
    onPhase?.("error");
    return { ok: false, error: FLUENTIZE_SHARE_FAILURE };
  } finally {
    if (inFlight) inFlight.current = false;
  }
}

export function durationFromPlayer(currentVideoId, player) {
  if (!currentVideoId || !player || typeof player.getDuration !== "function") {
    return { videoId: currentVideoId || "", seconds: 0 };
  }
  let reportedId = currentVideoId;
  try {
    const data = typeof player.getVideoData === "function" ? player.getVideoData() : null;
    if (data?.video_id) reportedId = data.video_id;
  } catch {
    return { videoId: currentVideoId, seconds: 0 };
  }
  if (reportedId !== currentVideoId) return { videoId: currentVideoId, seconds: 0 };
  let seconds = 0;
  try {
    seconds = Number(player.getDuration()) || 0;
  } catch {
    seconds = 0;
  }
  if (!(seconds > 0)) return { videoId: currentVideoId, seconds: 0 };
  return { videoId: currentVideoId, seconds };
}

export function createTaskTransport({
  player,
  getPlayer,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  gapMs = TASK_REPEAT_GAP_MS,
  onStarted,
} = {}) {
  const currentPlayer = () => (typeof getPlayer === "function" ? getPlayer() : player);
  let generation = 0;
  let timers = [];

  const drop = () => {
    generation += 1;
    timers.forEach((id) => clearTimer(id));
    timers = [];
  };

  const arm = (ms, fn, gen) => {
    const id = setTimer(() => {
      if (gen !== generation) return;
      fn();
    }, ms);
    timers.push(id);
  };

  const begin = (task, mode, videoDuration) => {
    const check = validateFluentizeTask(task, videoDuration);
    if (!check.ok) return { ok: false, errors: check.errors };
    const activePlayer = currentPlayer();
    if (!activePlayer || typeof activePlayer.seekTo !== "function" || typeof activePlayer.playVideo !== "function") {
      return { ok: false, errors: ["The video player is not ready."] };
    }
    drop();
    const gen = generation;
    const passes = mode === "repeat" ? check.repeat : 1;
    const span = Math.max(0, (check.stop - check.start) * 1000);
    let count = 0;
    const pass = () => {
      if (gen !== generation || count >= passes) return;
      const livePlayer = currentPlayer();
      if (!livePlayer || typeof livePlayer.seekTo !== "function" || typeof livePlayer.playVideo !== "function") return;
      count += 1;
      try {
        livePlayer.seekTo(check.start);
        livePlayer.playVideo();
      } catch {
        return;
      }
      if (count === 1 && typeof onStarted === "function") onStarted(task.id, mode);
      arm(span, () => {
        if (gen !== generation) return;
        const pausing = currentPlayer();
        if (typeof pausing?.pauseVideo === "function") pausing.pauseVideo();
        if (count < passes) arm(gapMs, pass, gen);
      }, gen);
    };
    pass();
    return { ok: true, mode, passes };
  };

  return {
    play(task, videoDuration) {
      return begin(task, "play", videoDuration);
    },
    repeat(task, videoDuration) {
      return begin(task, "repeat", videoDuration);
    },
    pause() {
      drop();
      const livePlayer = currentPlayer();
      if (typeof livePlayer?.pauseVideo === "function") livePlayer.pauseVideo();
    },
    stop(task) {
      drop();
      const livePlayer = currentPlayer();
      if (typeof livePlayer?.pauseVideo === "function") livePlayer.pauseVideo();
      const start = parseTaskClock(task?.startTime);
      if (start != null && typeof livePlayer?.seekTo === "function") livePlayer.seekTo(start);
    },
    cancel() {
      drop();
    },
  };
}
